import { useEffect, useState } from 'react'
import { supabase } from './supabaseClient'
import { AuthContext } from './useAuth'
import { applyMode, getStoredMode, setActiveUser } from './displayMode'
import { clearAllUserState } from './authCleanup'

// TEMP DIAGNOSTIC — remove once dev-logout is fully verified fixed.
const authDebug = (...args) => {
  // eslint-disable-next-line no-console
  console.log('[auth]', new Date().toISOString(), ...args)
}
if (typeof window !== 'undefined' && !window.__authDebugBanner) {
  window.__authDebugBanner = true
  authDebug('client supabase URL', supabase?.supabaseUrl ?? '(unknown)')
  authDebug('storage key present', !!Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token')))
}

// Idle-based auto-signout is DISABLED. Until smart event-aware timeouts
// land, the only logout paths are the explicit sign-out button or an
// explicit SIGNED_OUT event from Supabase.
//
// Loading semantics: `loading` starts true and goes false only after the
// initial getSession() resolves. AuthProvider does NOT gate child
// rendering on `loading`. ProtectedRoute reads `loading` and shows a
// spinner while it's true.
//
// IMPORTANT: this module exports ONLY AuthProvider so React Fast Refresh
// stays happy. The AuthContext object and the useAuth hook live in
// ./useAuth — see that file for the rationale.

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    // Track the previous session's user id so we can detect an account
    // switch on the same tab (User A logs out, User B logs in without a
    // page reload) and flush User A's state before User B's hydrates.
    // Supabase doesn't emit a dedicated USER_CHANGED event — SIGNED_IN
    // fires for both fresh and replacement sessions, so we compare ids.
    let prevUserId = null

    // Subscribe FIRST so an INITIAL_SESSION fired between getSession()'s
    // resolve and our subscription registration isn't lost.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        authDebug('onAuthStateChange', event, {
          hasSession: !!session,
          hasUser: !!session?.user,
          userId: session?.user?.id,
          expiresAt: session?.expires_at,
          accessTokenLast8: session?.access_token?.slice(-8),
        })
        if (cancelled) return
        if (event === 'SIGNED_OUT') {
          // Track 1.1 — single chokepoint flushes localStorage, the SWR
          // cache, the outbox, all realtime channels, and the displayMode
          // pointer in one shot. Replaces the old clearActiveOnLogout-only
          // path that left the cache + realtime channels behind.
          await clearAllUserState()
          setUser(null)
          setProfile(null)
          prevUserId = null
          return
        }
        // TOKEN_REFRESHED / INITIAL_SESSION / USER_UPDATED can fire with
        // a transiently-null session — only update on a real user,
        // otherwise keep current state so we don't nuke the auth tree.
        if (session?.user) {
          // Account-switch detector. A non-null prevUserId that doesn't
          // match this session means a different user just signed in on
          // the same tab; flush before hydrating.
          if (prevUserId && prevUserId !== session.user.id) {
            await clearAllUserState()
          }
          prevUserId = session.user.id
          setUser(session.user)
          fetchProfile(session.user.id)
          // Pin the active-user pointer + apply this user's stored mode.
          // main.jsx pre-paint already runs this for the cached active
          // user; this re-application covers a fresh sign-in (no prior
          // pointer) and an account switch on the same tab.
          setActiveUser(session.user.id)
          applyMode(getStoredMode(session.user.id))
        }
      }
    )

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      authDebug('getSession resolved', {
        hasSession: !!session,
        hasUser: !!session?.user,
        userId: session?.user?.id,
        expiresAt: session?.expires_at,
        error: error?.message,
      })
      if (cancelled) return
      if (session?.user) {
        setUser(session.user)
        fetchProfile(session.user.id, true)
      } else {
        // Definitive: no session in storage. Release the loading gate
        // so ProtectedRoute can redirect to /auth.
        setLoading(false)
      }
    })

    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [])

  const fetchProfile = async (userId, finalizeLoading = false) => {
    const { data } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .limit(1)
    setProfile(data?.[0] ?? null)
    if (finalizeLoading) setLoading(false)
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading }}>
      {children}
    </AuthContext.Provider>
  )
}
